import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import logger from '../utils/logger';
import { mockStaff } from '../mock/data';
import { DataService } from '../services/dataService';
import { AccountDataService } from '../services/accountDataService';
import { Customer as ExtendedCustomer } from '../utils/localStorage';
import { ExtendedReservation } from '../types/salonboard';
import { migrateExistingData } from '../scripts/migrate-to-salonboard';
import type { 
  ComprehensiveSettings, 
  SettingsAccess, 
  SettingsChangeLog
} from '../types/settings';
import type { Service } from '../types/service';
import { DEFAULT_SETTINGS } from '../types/settings';

// LocalStorage keys
const AI_CHAT_STORAGE_KEY = 'salonhub-ai-chat-history';
const SERVICES_STORAGE_KEY = 'salonhub-services';

// Helper functions for services persistence
const loadServicesFromStorage = (): Service[] => {
  try {
    const stored = localStorage.getItem(SERVICES_STORAGE_KEY);
    if (stored) {
      const services = JSON.parse(stored);
      logger.log('[Store] Loaded services from localStorage:', services);
      return services;
    }
  } catch (error) {
    logger.error('Failed to load services from localStorage:', error);
  }
  // デフォルトサービス（LocalStorageにデータがない場合）
  logger.log('[Store] Using default services (no data in localStorage)');
  return [
    {
      id: '1',
      name: 'ドライヘッドスパ60分',
      categoryId: 'headspa',
      category: 'ヘッドスパ',
      duration: 60,
      basePrice: 8500,
      price: 8500,
      taxRate: 0.1,
      isActive: true,
      displayOrder: 1,
      type: 'normal',
      availableStaff: [],
      requiredSkillLevel: 'beginner',
      advanceBookingDays: 30,
      minBookingNotice: 60,
      bufferTime: 15,
      options: [],
      tags: [],
      popularity: 80,
      requiredResources: [],
      statistics: {
        totalBookings: 0,
        revenue: 0,
        averageRating: 0,
        cancellationRate: 0,
        noShowRate: 0
      },
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: '2',
      name: 'アロマヘッドスパ45分',
      categoryId: 'headspa',
      category: 'ヘッドスパ',
      duration: 45,
      basePrice: 6500,
      price: 6500,
      taxRate: 0.1,
      isActive: true,
      displayOrder: 2,
      type: 'normal',
      availableStaff: [],
      requiredSkillLevel: 'beginner',
      advanceBookingDays: 30,
      minBookingNotice: 60,
      bufferTime: 15,
      options: [],
      tags: [],
      popularity: 70,
      requiredResources: [],
      statistics: {
        totalBookings: 0,
        revenue: 0,
        averageRating: 0,
        cancellationRate: 0,
        noShowRate: 0
      },
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: '3',
      name: 'プレミアムヘッドスパ90分',
      categoryId: 'headspa',
      category: 'ヘッドスパ',
      duration: 90,
      basePrice: 12000,
      price: 12000,
      taxRate: 0.1,
      isActive: true,
      displayOrder: 3,
      type: 'normal',
      availableStaff: [],
      requiredSkillLevel: 'intermediate',
      advanceBookingDays: 30,
      minBookingNotice: 60,
      bufferTime: 15,
      options: [],
      tags: [],
      popularity: 90,
      requiredResources: [],
      statistics: {
        totalBookings: 0,
        revenue: 0,
        averageRating: 0,
        cancellationRate: 0,
        noShowRate: 0
      },
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: '4',
      name: 'ベーシックヘッドスパ30分',
      categoryId: 'headspa',
      category: 'ヘッドスパ',
      duration: 30,
      basePrice: 4500,
      price: 4500,
      taxRate: 0.1,
      isActive: true,
      displayOrder: 4,
      type: 'normal',
      availableStaff: [],
      requiredSkillLevel: 'beginner',
      advanceBookingDays: 30,
      minBookingNotice: 60,
      bufferTime: 15,
      options: [],
      tags: [],
      popularity: 60,
      requiredResources: [],
      statistics: {
        totalBookings: 0,
        revenue: 0,
        averageRating: 0,
        cancellationRate: 0,
        noShowRate: 0
      },
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];
};

const saveServicesToStorage = (services: Service[]): void => {
  try {
    localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(services));
    logger.log('[Store] Saved services to localStorage:', services);
  } catch (error) {
    logger.error('Failed to save services to localStorage:', error);
  }
};

export type PlanType = 'basic' | 'standard' | 'premium' | 'enterprise';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'staff';
  store: {
    name: string;
    type: string;
  };
}

export interface StoreData {
  monthlySales: number;
  monthlyCustomers: number;
  averageSpending: number;
  repeatRate: number;
  newCustomers: number;
  customerSatisfaction: number;
  staffCount: number;
}

export interface PlanFeatures {
  aiAnalysis: boolean;
  aiChatLimit: number | null; // null = unlimited
  benchmarkAccess: boolean;
  advancedReports: boolean;
  customDashboard: boolean;
  apiAccess: boolean;
  voiceInput: boolean;
  // New features
  onboarding: boolean;
  emergency: boolean;
  batchOperations: boolean;
  advancedRecording: boolean;
  inventoryManagement: boolean | 'limited' | 'advanced';
}

export interface OnboardingState {
  isFirstTime: boolean;
  currentStep: number;
  completedTutorials: string[];
  beginnerMode: boolean;
  progressPercentage: number;
}

export interface EmergencyState {
  isActive: boolean;
  incidentType: 'medical' | 'allergy' | 'accident' | null;
  startTime: Date | null;
  involvedCustomer: string | null;
}

export interface BatchState {
  isMultiSelectMode: boolean;
  selectedItems: string[];
  currentOperation: string | null;
  progress: number;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface AIState {
  messages: ChatMessage[];
  isOpen: boolean;
  isMinimized: boolean;
}

// Notification State Types
export type NotificationPriority = 'critical' | 'high' | 'medium' | 'low';
export type NotificationCategory = 'reservation' | 'customer' | 'system' | 'staff';

export interface StoreNotification {
  id: string;
  icon: React.ReactElement;
  primary: string;
  secondary: string;
  timestamp: number;
  priority: NotificationPriority;
  isNew: boolean;
  isRead: boolean;
  onClick: () => void;
  actionLabel?: string;
  category?: NotificationCategory;
}

export type PeriodFilter = 'today' | 'week' | 'month' | 'all';
export type CategoryFilter = 'all' | 'reservation' | 'customer' | 'system' | 'staff';

interface NotificationState {
  notifications: StoreNotification[];
  unreadCount: number;
  isModalOpen: boolean;
  periodFilter: PeriodFilter;
  categoryFilter: CategoryFilter;
}

export interface TreatmentNote {
  id: string;
  timestamp: Date;
  content: string;
  type: 'template' | 'custom' | 'voice';
}

export interface TreatmentState {
  isActive: boolean;
  customerId: string | null;
  startTime: Date | null;
  notes: TreatmentNote[];
  hygieneFriendlyMode: boolean;
}

export interface InventoryAlert {
  id: string;
  itemName: string;
  currentLevel: number;
  threshold: number;
  severity: 'critical' | 'warning' | 'info';
  category: 'chemical' | 'consumable' | 'tool';
}

export interface CustomInventoryItem {
  id: string;
  name: string;
  category: string;
  threshold: number;
  unit: string;
}

export interface InventoryState {
  alerts: InventoryAlert[];
  customItems: CustomInventoryItem[];
  lastChecked: Date;
}



export interface Staff {
  id: string;
  name: string;
  role: string;
  experience: string;
  specialties: string[];
  schedule: string;
  performance: {
    customerSatisfaction: number;
    monthlyRevenue: number;
    repeatCustomers: number;
  };
}

export interface Reservation {
  id: string;
  customerId: string;
  customerName: string;
  service: string;
  staff: string;
  date: string;
  time: string;
  startTime: string;
  endTime: string;
  duration: number;
  price: number;
  status: string;
  notes?: string;
}

export interface AppState {
  // User & Authentication
  user: User | null;
  isAuthenticated: boolean;
  
  // Plan & Subscription
  currentPlan: PlanType;
  planFeatures: PlanFeatures;
  
  // Store Data
  storeData: StoreData;
  
  // UI State
  isSidebarOpen: boolean;
  currentPage: string;
  selectedCustomerId: number | null;
  salesActiveTab: number;
  
  // AI Usage
  aiUsage: Record<string, number>;
  
  // Notification State
  notification: NotificationState;
  
  // Data (Updated for SalonBoard compatibility)
  customers: ExtendedCustomer[];
  reservations: ExtendedReservation[];
  staff: Staff[];
  services: Service[];
  
  // New Features State
  onboarding: OnboardingState;
  emergency: EmergencyState;
  batch: BatchState;
  treatment: TreatmentState;
  inventory: InventoryState;
  ai: AIState;
  
  // Settings Management State (Issue #238)
  settings: ComprehensiveSettings;
  settingsAccess: SettingsAccess;
  settingsChangeLog: SettingsChangeLog[];
  settingsLoading: boolean;
  settingsError: string | null;
  
  // Shift Management State
  shiftExport: {
    exportShifts: (format: 'json' | 'csv' | 'xlsx', shifts: Record<string, unknown>[], staffList: Record<string, unknown>[], exportData: Record<string, unknown>) => Promise<Blob>;
    externalServices: Map<string, Record<string, unknown>>;
    publishSettings: Map<string, Record<string, unknown>>;
  };
  
  // Actions
  setUser: (user: User) => void;
  updateStoreName: (name: string) => void;
  setPlan: (plan: PlanType) => void;
  setStoreData: (data: Partial<StoreData>) => void;
  setSidebarOpen: (isOpen: boolean) => void;
  setCurrentPage: (page: string) => void;
  setSelectedCustomerId: (customerId: number | null) => void;
  setSalesActiveTab: (tab: number) => void;
  incrementAIUsage: (feature: string) => void;
  checkFeatureAccess: (feature: string) => boolean;
  setServices: (services: Service[]) => void;
  logout: () => void;
  
  // New Feature Actions
  setBeginnerMode: (enabled: boolean) => void;
  updateOnboardingProgress: (step: number, percentage: number) => void;
  completeTutorial: (tutorialId: string) => void;
  activateEmergency: (type: EmergencyState['incidentType'], customerId?: string) => void;
  deactivateEmergency: () => void;
  setMultiSelectMode: (enabled: boolean) => void;
  toggleBatchSelection: (itemId: string) => void;
  clearBatchSelection: () => void;
  setBatchOperation: (operation: string | null, progress?: number) => void;
  activateTreatmentMode: (customerId: string) => void;
  deactivateTreatmentMode: () => void;
  addTreatmentNote: (note: Omit<TreatmentNote, 'id' | 'timestamp'>) => void;
  setHygieneFriendlyMode: (enabled: boolean) => void;
  setTreatmentMode: (treatment: Partial<TreatmentState>) => void;
  updateInventoryAlerts: (alerts: InventoryAlert[]) => void;
  addCustomInventoryItem: (item: Omit<CustomInventoryItem, 'id'>) => void;
  
  // AI Chat Actions
  addAIMessage: (message: ChatMessage) => void;
  clearAIMessages: () => void;
  toggleAIChat: () => void;
  setAIMinimized: (minimized: boolean) => void;
  loadAIFromLocalStorage: () => void;
  saveAIToLocalStorage: () => void;
  
  // Notification Actions
  addNotification: (notification: Omit<StoreNotification, 'id' | 'timestamp' | 'isRead'>) => void;
  markNotificationAsRead: (notificationId: string) => void;
  markAllNotificationsAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  toggleNotificationModal: () => void;
  setNotificationModalOpen: (isOpen: boolean) => void;
  getUnreadNotifications: () => StoreNotification[];
  initializeNotifications: () => void;
  setPeriodFilter: (filter: PeriodFilter) => void;
  setCategoryFilter: (filter: CategoryFilter) => void;
  
  // Customer Management Actions (DataService統合 - 非同期)
  loadCustomers: () => Promise<void>;
  addCustomer: (customerData: Omit<ExtendedCustomer, 'id' | 'createdAt' | 'updatedAt' | 'customerCode' | 'messageSettings' | 'deliveryHistory' | 'mergeHistory' | 'mergeCandidates' | 'customFields'>) => Promise<ExtendedCustomer>;
  updateCustomer: (id: number, updates: Partial<ExtendedCustomer>) => Promise<void>;
  deleteCustomer: (id: number) => Promise<void>;
  
  // Reservation Management Actions (DataService統合 - 非同期)
  loadReservations: () => Promise<void>;
  addReservation: (reservation: ExtendedReservation) => Promise<void>;
  updateReservation: (id: string, updates: Partial<ExtendedReservation>) => Promise<void>;
  deleteReservation: (id: string) => Promise<void>;
  
  // Settings Management Actions (Issue #238 - DataService統合)
  loadSettings: () => Promise<void>;
  updateSettings: <K extends keyof ComprehensiveSettings>(
    section: K, 
    updates: Partial<ComprehensiveSettings[K]>
  ) => Promise<void>;
  resetSettings: (section?: keyof ComprehensiveSettings) => Promise<void>;
  exportSettings: (format: 'json' | 'csv') => Promise<Blob>;
  importSettings: (settingsData: Partial<ComprehensiveSettings>) => Promise<void>;
  validateSettings: (settings: Partial<ComprehensiveSettings>) => Promise<boolean>;
  getSettingsAccess: (userRole: string) => SettingsAccess;
  
  // Data Migration
  runDataMigration: () => Promise<void>;
}

const planFeatureMap: Record<PlanType, PlanFeatures> = {
  basic: {
    aiAnalysis: true,  // 基本AI分析は利用可能
    aiChatLimit: 10,
    benchmarkAccess: false,
    advancedReports: false,
    customDashboard: false,
    apiAccess: false,
    voiceInput: false,
    // New features
    onboarding: true,
    emergency: false,
    batchOperations: false,
    advancedRecording: false,
    inventoryManagement: false,
  },
  standard: {
    aiAnalysis: true,
    aiChatLimit: 50,
    benchmarkAccess: false,
    advancedReports: true,
    customDashboard: false,
    apiAccess: false,
    voiceInput: false,
    // New features
    onboarding: true,
    emergency: true,
    batchOperations: true,
    advancedRecording: true,
    inventoryManagement: 'limited',
  },
  premium: {
    aiAnalysis: true,
    aiChatLimit: null, // unlimited
    benchmarkAccess: true,
    advancedReports: true,
    customDashboard: true,
    apiAccess: true,
    voiceInput: false,
    // New features
    onboarding: true,
    emergency: true,
    batchOperations: true,
    advancedRecording: true,
    inventoryManagement: 'advanced',
  },
  enterprise: {
    aiAnalysis: true,
    aiChatLimit: null, // unlimited
    benchmarkAccess: true,
    advancedReports: true,
    customDashboard: true,
    apiAccess: true,
    voiceInput: true,
    // New features
    onboarding: true,
    emergency: true,
    batchOperations: true,
    advancedRecording: true,
    inventoryManagement: 'advanced',
  },
};

// データ移行の自動実行 - 初回のみ実行
const initializeData = async () => {
  try {
    // 既に移行済みかチェック
    const migrationFlag = localStorage.getItem('salonhub-data-migration-completed');
    if (migrationFlag !== 'true') {
      await migrateExistingData();
      localStorage.setItem('salonhub-data-migration-completed', 'true');
    }
  } catch (error) {
    logger.error('Data migration failed:', error);
  }
};

// 初期化時にデータ移行を実行
initializeData();

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
  // Initial state
  user: {
    id: '1',
    name: '田中美香',
    email: 'tanaka@hitoyasumi.jp',
    role: 'owner',
    store: {
      name: 'ドライヘッドスパHITOYASUMI 渋谷店',
      type: 'ヘッドスパ専門店',
    },
  },
  isAuthenticated: true,
  currentPlan: 'standard', // Start with standard for demo
  planFeatures: planFeatureMap.standard,
  storeData: {
    monthlySales: 2800000,
    monthlyCustomers: 320,
    averageSpending: 8500,
    repeatRate: 85,
    newCustomers: 45,
    customerSatisfaction: 4.8,
    staffCount: 5,
  },
  isSidebarOpen: true,
  currentPage: 'dashboard',
  selectedCustomerId: null,
  salesActiveTab: 0,
  aiUsage: {},
  
  // Data (遅延読み込み - App.tsxの初期化後に読み込まれる)
  customers: [], // Will be loaded by loadCustomers() after App initialization
  reservations: [], // Will be loaded by loadReservations() after App initialization
  staff: mockStaff,
  services: loadServicesFromStorage(),
  
  // New Features Initial State
  onboarding: {
    isFirstTime: true,
    currentStep: 0,
    completedTutorials: [],
    beginnerMode: false,
    progressPercentage: 0,
  },
  emergency: {
    isActive: false,
    incidentType: null,
    startTime: null,
    involvedCustomer: null,
  },
  batch: {
    isMultiSelectMode: false,
    selectedItems: [],
    currentOperation: null,
    progress: 0,
  },
  treatment: {
    isActive: false,
    customerId: null,
    startTime: null,
    notes: [],
    hygieneFriendlyMode: false,
  },
  inventory: {
    alerts: [
      {
        id: '1',
        itemName: 'カラー剤（ブラウン）',
        currentLevel: 2,
        threshold: 5,
        severity: 'critical',
        category: 'chemical',
      },
      {
        id: '2',
        itemName: 'シャンプー',
        currentLevel: 8,
        threshold: 10,
        severity: 'warning',
        category: 'consumable',
      },
      {
        id: '3',
        itemName: 'タオル',
        currentLevel: 15,
        threshold: 20,
        severity: 'info',
        category: 'consumable',
      },
    ],
    customItems: [],
    lastChecked: new Date(),
  },
  ai: {
    messages: [],
    isOpen: false,
    isMinimized: false,
  },
  
  // Notification State
  notification: {
    notifications: [],
    unreadCount: 0,
    isModalOpen: false,
    periodFilter: 'all' as PeriodFilter,
    categoryFilter: 'all' as CategoryFilter,
  },
  
  // Settings Management State (Issue #238)
  settings: DEFAULT_SETTINGS,
  settingsAccess: {
    canViewStore: true,
    canEditStore: true, 
    canManageUsers: true,
    canConfigureIntegrations: true,
    canAccessDataSettings: true,
    canModifyAISettings: true,
    canViewSystemSettings: true,
    canEditSystemSettings: true,
  },
  settingsChangeLog: [],
  settingsLoading: false,
  settingsError: null,
  
  // Shift Management State
  shiftExport: {
    exportShifts: async (format, _shifts, _staffList, exportData) => {
      // This will be implemented by the actual export functions
      logger.log('Exporting shifts:', { format, exportData });
      return new Blob(['Export data'], { type: 'application/octet-stream' });
    },
    externalServices: new Map(),
    publishSettings: new Map(),
  },
  
  // Actions
  setUser: (user) => set({ user }),
  
  updateStoreName: (name) => set((state) => ({
    user: state.user ? {
      ...state.user,
      store: {
        ...state.user.store,
        name
      }
    } : null
  })),
  
  setPlan: (plan) => set({ 
    currentPlan: plan, 
    planFeatures: planFeatureMap[plan] 
  }),
  
  setStoreData: (data) => set((state) => ({
    storeData: { ...state.storeData, ...data }
  })),
  
  setSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }),
  
  setCurrentPage: (page) => set({ currentPage: page }),
  
  setSelectedCustomerId: (customerId) => set({ selectedCustomerId: customerId }),
  
  setSalesActiveTab: (tab) => set({ salesActiveTab: tab }),

  setServices: (services) => {
    saveServicesToStorage(services);
    set({ services });
  },

  incrementAIUsage: (feature) => set((state) => ({
    aiUsage: {
      ...state.aiUsage,
      [feature]: (state.aiUsage[feature] || 0) + 1,
    }
  })),
  
  checkFeatureAccess: (feature) => {
    const state = get();
    const { planFeatures, aiUsage } = state;
    
    switch (feature) {
      case 'aiChat':
        if (!planFeatures.aiAnalysis) return false;
        if (planFeatures.aiChatLimit === null) return true;
        return (aiUsage.aiChat || 0) < planFeatures.aiChatLimit;
        
      case 'benchmarkAnalysis':
        return planFeatures.benchmarkAccess;
        
      case 'advancedReports':
        return planFeatures.advancedReports;
        
      case 'customDashboard':
        return planFeatures.customDashboard;
        
      case 'apiAccess':
        return planFeatures.apiAccess;
        
      case 'voiceInput':
        return planFeatures.voiceInput;
        
      case 'onboarding':
        return planFeatures.onboarding;
        
      case 'emergency':
        return planFeatures.emergency;
        
      case 'batchOperations':
        return planFeatures.batchOperations;
        
      case 'advancedRecording':
        return planFeatures.advancedRecording;
        
      case 'inventoryManagement':
        return Boolean(planFeatures.inventoryManagement);
        
      default:
        return true;
    }
  },
  
  // New Feature Actions
  setBeginnerMode: (enabled) => set((state) => ({
    onboarding: { ...state.onboarding, beginnerMode: enabled }
  })),
  
  updateOnboardingProgress: (step, percentage) => set((state) => ({
    onboarding: { ...state.onboarding, currentStep: step, progressPercentage: percentage }
  })),
  
  completeTutorial: (tutorialId) => set((state) => ({
    onboarding: {
      ...state.onboarding,
      completedTutorials: [...state.onboarding.completedTutorials, tutorialId]
    }
  })),
  
  activateEmergency: (type, customerId) => set({
    emergency: {
      isActive: true,
      incidentType: type,
      startTime: new Date(),
      involvedCustomer: customerId || null,
    }
  }),
  
  deactivateEmergency: () => set({
    emergency: {
      isActive: false,
      incidentType: null,
      startTime: null,
      involvedCustomer: null,
    }
  }),
  
  setMultiSelectMode: (enabled) => set((state) => ({
    batch: {
      ...state.batch,
      isMultiSelectMode: enabled,
      selectedItems: enabled ? state.batch.selectedItems : [],
    }
  })),
  
  toggleBatchSelection: (itemId) => set((state) => {
    const isSelected = state.batch.selectedItems.includes(itemId);
    return {
      batch: {
        ...state.batch,
        selectedItems: isSelected
          ? state.batch.selectedItems.filter(id => id !== itemId)
          : [...state.batch.selectedItems, itemId],
      }
    };
  }),
  
  clearBatchSelection: () => set((state) => ({
    batch: { ...state.batch, selectedItems: [], currentOperation: null, progress: 0 }
  })),
  
  setBatchOperation: (operation, progress) => set((state) => ({
    batch: { ...state.batch, currentOperation: operation, progress: progress || 0 }
  })),
  
  activateTreatmentMode: (customerId) => set({
    treatment: {
      isActive: true,
      customerId,
      startTime: new Date(),
      notes: [],
      hygieneFriendlyMode: false,
    }
  }),
  
  deactivateTreatmentMode: () => set({
    treatment: {
      isActive: false,
      customerId: null,
      startTime: null,
      notes: [],
      hygieneFriendlyMode: false,
    }
  }),
  
  addTreatmentNote: (note) => set((state) => ({
    treatment: {
      ...state.treatment,
      notes: [
        ...state.treatment.notes,
        {
          ...note,
          id: Date.now().toString(),
          timestamp: new Date(),
        }
      ]
    }
  })),
  
  setHygieneFriendlyMode: (enabled) => set((state) => ({
    treatment: { ...state.treatment, hygieneFriendlyMode: enabled }
  })),
  
  setTreatmentMode: (treatment) => set((state) => ({
    treatment: { ...state.treatment, ...treatment }
  })),
  
  updateInventoryAlerts: (alerts) => set((state) => ({
    inventory: { ...state.inventory, alerts, lastChecked: new Date() }
  })),
  
  addCustomInventoryItem: (item) => set((state) => ({
    inventory: {
      ...state.inventory,
      customItems: [
        ...state.inventory.customItems,
        { ...item, id: Date.now().toString() }
      ]
    }
  })),
  
  // AI Chat Actions
  addAIMessage: (message) => set((state) => {
    const newMessages = [...state.ai.messages, message];
    // Save to LocalStorage
    try {
      localStorage.setItem(AI_CHAT_STORAGE_KEY, JSON.stringify({
        messages: newMessages,
        lastUpdated: new Date().toISOString()
      }));
    } catch (error) {
      logger.error('Failed to save AI chat to localStorage:', error);
    }
    return { ai: { ...state.ai, messages: newMessages } };
  }),
  
  clearAIMessages: () => set((state) => {
    // Clear from LocalStorage
    try {
      localStorage.removeItem(AI_CHAT_STORAGE_KEY);
    } catch (error) {
      logger.error('Failed to clear AI chat from localStorage:', error);
    }
    return { ai: { ...state.ai, messages: [] } };
  }),
  
  toggleAIChat: () => set((state) => ({
    ai: { ...state.ai, isOpen: !state.ai.isOpen, isMinimized: false }
  })),
  
  setAIMinimized: (minimized) => set((state) => ({
    ai: { ...state.ai, isMinimized: minimized }
  })),
  
  loadAIFromLocalStorage: () => {
    try {
      const stored = localStorage.getItem(AI_CHAT_STORAGE_KEY);
      if (stored) {
        const { messages } = JSON.parse(stored);
        // Convert timestamp strings to Date objects
        const parsedMessages = messages.map((msg: { timestamp: string | Date; [key: string]: unknown }) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        set((state) => ({ ai: { ...state.ai, messages: parsedMessages } }));
      }
    } catch (error) {
      logger.error('Failed to load AI chat from localStorage:', error);
    }
  },
  
  saveAIToLocalStorage: () => {
    const state = get();
    try {
      localStorage.setItem(AI_CHAT_STORAGE_KEY, JSON.stringify({
        messages: state.ai.messages,
        lastUpdated: new Date().toISOString()
      }));
    } catch (error) {
      logger.error('Failed to save AI chat to localStorage:', error);
    }
  },
  
  // Notification Actions
  addNotification: (notification) => set((state) => {
    const newNotification: StoreNotification = {
      ...notification,
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      isRead: false,
    };
    
    const updatedNotifications = [newNotification, ...state.notification.notifications];
    const unreadCount = updatedNotifications.filter(n => !n.isRead).length;
    
    logger.log('➕ Notification added:', newNotification.id);
    
    return {
      notification: {
        ...state.notification,
        notifications: updatedNotifications,
        unreadCount,
      }
    };
  }),
  
  markNotificationAsRead: (notificationId) => set((state) => {
    const updatedNotifications = state.notification.notifications.map(n =>
      n.id === notificationId ? { ...n, isRead: true, isNew: false } : n
    );
    const unreadCount = updatedNotifications.filter(n => !n.isRead).length;
    
    logger.log(`✅ Notification ${notificationId} marked as read`);
    
    return {
      notification: {
        ...state.notification,
        notifications: updatedNotifications,
        unreadCount,
      }
    };
  }),
  
  markAllNotificationsAsRead: () => set((state) => {
    const updatedNotifications = state.notification.notifications.map(n => ({
      ...n,
      isRead: true,
      isNew: false,
    }));
    
    logger.log('✅ All notifications marked as read');
    
    return {
      notification: {
        ...state.notification,
        notifications: updatedNotifications,
        unreadCount: 0,
      }
    };
  }),
  
  deleteNotification: (notificationId) => set((state) => {
    const updatedNotifications = state.notification.notifications.filter(
      n => n.id !== notificationId
    );
    const unreadCount = updatedNotifications.filter(n => !n.isRead).length;
    
    logger.log(`🗑️ Notification ${notificationId} deleted`);
    
    return {
      notification: {
        ...state.notification,
        notifications: updatedNotifications,
        unreadCount,
      }
    };
  }),
  
  clearAllNotifications: () => set((state) => {
    logger.log('🗑️ All notifications cleared');
    
    return {
      notification: {
        ...state.notification,
        notifications: [],
        unreadCount: 0,
      }
    };
  }),
  
  toggleNotificationModal: () => set((state) => ({
    notification: {
      ...state.notification,
      isModalOpen: !state.notification.isModalOpen,
    }
  })),
  
  setNotificationModalOpen: (isOpen) => set((state) => ({
    notification: {
      ...state.notification,
      isModalOpen: isOpen,
    }
  })),
  
  getUnreadNotifications: () => {
    const state = get();
    return state.notification.notifications.filter(n => !n.isRead);
  },
  
  initializeNotifications: () => {
    // Header.tsxからの通知データを初期化用に使用可能
    // 実際の実装ではAPIやLocalStorageから取得することも可能
    logger.log('🔔 Notifications initialized');
  },

  setPeriodFilter: (filter) => set((state) => {
    logger.log(`⏰ Period filter changed to: ${filter}`);
    return {
      notification: {
        ...state.notification,
        periodFilter: filter,
      }
    };
  }),

  setCategoryFilter: (filter) => set((state) => {
    logger.log(`📂 Category filter changed to: ${filter}`);
    return {
      notification: {
        ...state.notification,
        categoryFilter: filter,
      }
    };
  }),
  
  // Customer Management Actions (DataService統合)
  loadCustomers: async () => {
    try {
      logger.log('🔄 Loading customers via AccountDataService...');
      const customers = AccountDataService.getCustomers();
      set({ customers });
      logger.log(`✅ Loaded ${customers.length} customers successfully`);
    } catch (error) {
      logger.error('❌ Failed to load customers:', error);
      // UI保護: エラー時も既存データ保持
      set(state => ({ customers: state.customers }));
    }
  },
  
  addCustomer: async (customerData) => {
    try {
      logger.log('➕ Adding customer via DataService...');
      const newCustomer = await DataService.addCustomer(customerData);
      set((state) => ({ customers: [...state.customers, newCustomer] }));
      logger.log('✅ Customer added successfully:', newCustomer.id);
      return newCustomer;
    } catch (error) {
      logger.error('❌ Failed to add customer:', error);
      throw error; // UI layer でエラーハンドリング
    }
  },
  
  updateCustomer: async (id, updates) => {
    try {
      logger.log(`🔄 Updating customer ${id} via DataService...`);
      const updatedCustomer = await DataService.updateCustomer(id, updates);
      set((state) => ({
        customers: state.customers.map(c => c.id === id ? updatedCustomer : c)
      }));
      logger.log('✅ Customer updated successfully:', id);
    } catch (error) {
      logger.error(`❌ Failed to update customer ${id}:`, error);
      throw error; // UI layer でエラーハンドリング
    }
  },
  
  deleteCustomer: async (id) => {
    try {
      logger.log(`🗑️ Deleting customer ${id} via DataService...`);
      await DataService.deleteCustomer(id);
      set((state) => ({
        customers: state.customers.filter(c => c.id !== id)
      }));
      logger.log('✅ Customer deleted successfully:', id);
    } catch (error) {
      logger.error(`❌ Failed to delete customer ${id}:`, error);
      throw error; // UI layer でエラーハンドリング
    }
  },
  
  // Reservation Management Actions (DataService統合)
  loadReservations: async () => {
    try {
      logger.log('📅 Loading reservations via DataService...');
      const reservations = await DataService.getReservations();
      set({ reservations });
      logger.log(`✅ Loaded ${reservations.length} reservations successfully`);
    } catch (error) {
      logger.error('❌ Failed to load reservations:', error);
      // UI保護: エラー時も既存データ保持
      set(state => ({ reservations: state.reservations }));
    }
  },
  
  addReservation: async (reservation) => {
    try {
      logger.log('➕ Adding reservation via DataService...');
      // DataServiceにreservation個別追加メソッドがない場合は全体更新を使用
      const currentReservations = await DataService.getReservations();
      const updatedReservations = [...currentReservations, reservation];
      await DataService.saveReservations(updatedReservations);
      set({ reservations: updatedReservations });
      logger.log('✅ Reservation added successfully:', reservation.id);
    } catch (error) {
      logger.error('❌ Failed to add reservation:', error);
      throw error; // UI layer でエラーハンドリング
    }
  },
  
  updateReservation: async (id, updates) => {
    try {
      logger.log(`🔄 Updating reservation ${id} via DataService...`);
      const currentReservations = await DataService.getReservations();
      const updatedReservations = currentReservations.map(r => 
        r.id === id ? { ...r, ...updates } : r
      );
      await DataService.saveReservations(updatedReservations);
      set({ reservations: updatedReservations });
      logger.log('✅ Reservation updated successfully:', id);
    } catch (error) {
      logger.error(`❌ Failed to update reservation ${id}:`, error);
      throw error; // UI layer でエラーハンドリング
    }
  },
  
  deleteReservation: async (id) => {
    try {
      logger.log(`🗑️ Deleting reservation ${id} via DataService...`);
      const currentReservations = await DataService.getReservations();
      const updatedReservations = currentReservations.filter(r => r.id !== id);
      await DataService.saveReservations(updatedReservations);
      set({ reservations: updatedReservations });
      logger.log('✅ Reservation deleted successfully:', id);
    } catch (error) {
      logger.error(`❌ Failed to delete reservation ${id}:`, error);
      throw error; // UI layer でエラーハンドリング
    }
  },
  
  // Data Migration (統一フラグによる管理)
  runDataMigration: async () => {
    try {
      // 統一フラグをチェック - App.tsxで処理済みの場合はスキップ
      const isInitialized = localStorage.getItem('SALONHUB_COMPLETE_SETUP_v2');
      if (isInitialized === 'true') {
        logger.log('✅ Data migration already handled by App.tsx');
        return;
      }
      
      const result = await migrateExistingData();
      if (result.success) {
        // データ移行後にDataService経由で最新データを読み込む
        logger.log('🔄 Reloading data after migration via DataService...');
        const [customers, reservations] = await Promise.all([
          DataService.getCustomers(),
          DataService.getReservations()
        ]);
        set({ customers, reservations });
        logger.log('✅ Data reloaded successfully after migration');
      }
    } catch (error) {
      logger.error('Data migration failed in store:', error);
    }
  },
  
  // Settings Management Actions (Issue #238 - DataService統合)
  loadSettings: async () => {
    try {
      set({ settingsLoading: true, settingsError: null });
      logger.log('⚙️ Loading settings via DataService...');
      
      // DataServiceからsettings取得 (将来実装)
      // 現在はlocalStorageフォールバック
      const stored = localStorage.getItem('salonhub-settings');
      const loadedSettings = stored ? JSON.parse(stored) : DEFAULT_SETTINGS;
      
      set({ 
        settings: { ...DEFAULT_SETTINGS, ...loadedSettings },
        settingsLoading: false 
      });
      logger.log('✅ Settings loaded successfully');
    } catch (error) {
      logger.error('❌ Failed to load settings:', error);
      set({ 
        settingsError: error instanceof Error ? error.message : 'Settings load failed',
        settingsLoading: false 
      });
    }
  },
  
  updateSettings: async (section, updates) => {
    try {
      set({ settingsLoading: true, settingsError: null });
      logger.log(`⚙️ Updating settings section: ${section}`);
      
      const state = get();
      const currentSection = state.settings?.[section] || {};
      const updatedSettings = {
        ...state.settings,
        [section]: { ...currentSection, ...updates },
        lastUpdated: new Date(),
        updatedBy: state.user?.id || 'unknown'
      };
      
      // 変更ログを記録
      const changeLog: SettingsChangeLog = {
        id: Date.now().toString(),
        timestamp: new Date(),
        userId: state.user?.id || 'unknown',
        userName: state.user?.name || 'Unknown',
        section,
        field: Object.keys(updates)[0] || 'multiple',
        oldValue: currentSection,
        newValue: { ...currentSection, ...updates },
        ipAddress: 'localhost', // 将来実装
      };
      
      // 永続化 (DataService統合は将来実装)
      localStorage.setItem('salonhub-settings', JSON.stringify(updatedSettings));
      
      set({ 
        settings: updatedSettings,
        settingsChangeLog: [...state.settingsChangeLog, changeLog].slice(-100), // 最新100件保持
        settingsLoading: false 
      });
      
      logger.log(`✅ Settings section ${section} updated successfully`);
    } catch (error) {
      logger.error(`❌ Failed to update settings section ${section}:`, error);
      set({ 
        settingsError: error instanceof Error ? error.message : 'Settings update failed',
        settingsLoading: false 
      });
      throw error;
    }
  },
  
  resetSettings: async (section) => {
    try {
      set({ settingsLoading: true, settingsError: null });
      
      if (section) {
        logger.log(`🔄 Resetting settings section: ${section}`);
        const state = get();
        const resetSettings = {
          ...state.settings,
          [section]: DEFAULT_SETTINGS[section],
          lastUpdated: new Date(),
          updatedBy: state.user?.id || 'system'
        };
        
        localStorage.setItem('salonhub-settings', JSON.stringify(resetSettings));
        set({ settings: resetSettings, settingsLoading: false });
        logger.log(`✅ Settings section ${section} reset successfully`);
      } else {
        logger.log('🔄 Resetting all settings to default');
        localStorage.setItem('salonhub-settings', JSON.stringify(DEFAULT_SETTINGS));
        set({ settings: DEFAULT_SETTINGS, settingsLoading: false });
        logger.log('✅ All settings reset to default');
      }
    } catch (error) {
      logger.error('❌ Failed to reset settings:', error);
      set({ 
        settingsError: error instanceof Error ? error.message : 'Settings reset failed',
        settingsLoading: false 
      });
      throw error;
    }
  },
  
  exportSettings: async (format) => {
    try {
      const state = get();
      const exportData = {
        settings: state.settings,
        exportedAt: new Date().toISOString(),
        exportedBy: state.user?.name || 'Unknown',
        version: state.settings.version
      };
      
      let blob: Blob;
      let filename: string;
      
      if (format === 'json') {
        blob = new Blob([JSON.stringify(exportData, null, 2)], { 
          type: 'application/json' 
        });
        filename = `salonhub-settings-${new Date().toISOString().split('T')[0]}.json`;
      } else {
        // CSV format implementation
        const csv = Object.entries(exportData.settings)
          .map(([key, value]) => `${key},"${JSON.stringify(value).replace(/"/g, '""')}"`)
          .join('\n');
        blob = new Blob([csv], { type: 'text/csv' });
        filename = `salonhub-settings-${new Date().toISOString().split('T')[0]}.csv`;
      }
      
      logger.log(`📄 Settings exported as ${format.toUpperCase()}: ${filename}`);
      return blob;
    } catch (error) {
      logger.error('❌ Failed to export settings:', error);
      throw error;
    }
  },
  
  importSettings: async (settingsData) => {
    try {
      set({ settingsLoading: true, settingsError: null });
      logger.log('📥 Importing settings...');
      
      // バリデーション
      const isValid = await get().validateSettings(settingsData);
      if (!isValid) {
        throw new Error('Invalid settings data format');
      }
      
      const state = get();
      const mergedSettings = {
        ...DEFAULT_SETTINGS,
        ...settingsData,
        lastUpdated: new Date(),
        updatedBy: state.user?.id || 'import',
        version: DEFAULT_SETTINGS.version // 常に最新バージョンを使用
      };
      
      localStorage.setItem('salonhub-settings', JSON.stringify(mergedSettings));
      set({ settings: mergedSettings, settingsLoading: false });
      
      logger.log('✅ Settings imported successfully');
    } catch (error) {
      logger.error('❌ Failed to import settings:', error);
      set({ 
        settingsError: error instanceof Error ? error.message : 'Settings import failed',
        settingsLoading: false 
      });
      throw error;
    }
  },
  
  validateSettings: async (settings) => {
    try {
      // 基本的なバリデーション
      if (!settings || typeof settings !== 'object') return false;
      
      // 必須セクションの存在チェック
      const requiredSections = ['store', 'users', 'data', 'notifications', 'ai', 'display', 'system'] as const;
      const hasAllSections = requiredSections.every(section =>
        section in settings && typeof (settings as Record<string, unknown>)[section] === 'object'
      );
      
      if (!hasAllSections) {
        logger.warn('⚠️ Settings validation failed: missing required sections');
        return false;
      }
      
      // 店舗設定の詳細バリデーション
      const storeSettings = (settings as Record<string, unknown>).store as Record<string, unknown> | undefined;
      if (storeSettings) {
        const storeName = storeSettings.storeName;
        if (!storeName || typeof storeName !== 'string' || storeName.length === 0) {
          logger.warn('⚠️ Settings validation failed: store name is required');
          return false;
        }
        const taxRate = storeSettings.taxRate;
        if (taxRate && typeof taxRate === 'number' && (taxRate < 0 || taxRate > 1)) {
          logger.warn('⚠️ Settings validation failed: invalid tax rate');
          return false;
        }
      }
      
      logger.log('✅ Settings validation passed');
      return true;
    } catch (error) {
      logger.error('❌ Settings validation error:', error);
      return false;
    }
  },
  
  getSettingsAccess: (userRole) => {
    const roleAccessMap: Record<string, SettingsAccess> = {
      owner: {
        canViewStore: true,
        canEditStore: true,
        canManageUsers: true,
        canConfigureIntegrations: true,
        canAccessDataSettings: true,
        canModifyAISettings: true,
        canViewSystemSettings: true,
        canEditSystemSettings: true,
      },
      manager: {
        canViewStore: true,
        canEditStore: true,
        canManageUsers: false,
        canConfigureIntegrations: false,
        canAccessDataSettings: false,
        canModifyAISettings: false,
        canViewSystemSettings: false,
        canEditSystemSettings: false,
      },
      staff: {
        canViewStore: false,
        canEditStore: false,
        canManageUsers: false,
        canConfigureIntegrations: false,
        canAccessDataSettings: false,
        canModifyAISettings: false,
        canViewSystemSettings: false,
        canEditSystemSettings: false,
      },
    };
    
    return roleAccessMap[userRole] || roleAccessMap.staff;
  },
  
  logout: () => set({
    user: null,
    isAuthenticated: false,
    currentPlan: 'basic',
    planFeatures: planFeatureMap.basic,
    aiUsage: {},
    // Reset new features state
    onboarding: {
      isFirstTime: true,
      currentStep: 0,
      completedTutorials: [],
      beginnerMode: false,
      progressPercentage: 0,
    },
    emergency: {
      isActive: false,
      incidentType: null,
      startTime: null,
      involvedCustomer: null,
    },
    batch: {
      isMultiSelectMode: false,
      selectedItems: [],
      currentOperation: null,
      progress: 0,
    },
    treatment: {
      isActive: false,
      customerId: null,
      startTime: null,
      notes: [],
      hygieneFriendlyMode: false,
    },
    inventory: {
      alerts: [],
      customItems: [],
      lastChecked: new Date(),
    },
    ai: {
      messages: [],
      isOpen: false,
      isMinimized: false,
    },
    notification: {
      notifications: [],
      unreadCount: 0,
      isModalOpen: false,
      periodFilter: 'all' as PeriodFilter,
      categoryFilter: 'all' as CategoryFilter,
    },
  }),
  }),
  {
    name: 'salonhub-store', // LocalStorage key
    partialize: (state) => ({
      // Only persist these fields
      currentPlan: state.currentPlan,
      planFeatures: state.planFeatures,
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      services: state.services,
      // Exclude UI state and temporary data
    }),
  }
));